import { BaseQueryFn, createApi } from '@reduxjs/toolkit/query/react'
import {
  ICronJobRunsResponse,
  ICronJobsResponse,
  IRunCronJobResponse,
} from 'src/features/cron-jobs/interfaces/cron-jobs.interfaces'
import { IRoomTv } from 'src/features/home/interfaces/home.interfaces'
import {
  IQueueActivityParams,
  IQueueActivityResponse,
  IQueuesResponse,
} from 'src/features/cron-jobs/interfaces/queues.interfaces'
import { env } from '@/common/config/env'
import api from '@/common/helpers/api.helpers'
import { Method } from 'axios'

type CustomBaseQuery = BaseQueryFn<{ url: string; params?: any; method?: Method; data?: any }, unknown, unknown>

const customBaseQuery: CustomBaseQuery = async ({ url, params, method = 'GET', data }) => {
  try {
    const baseURL = env.RESIDEN_API_BASE_URL
    const result = await api(method, `${baseURL}${url}`, method === 'GET' ? params : data)
    return { data: result.data }
  } catch (error: any) {
    return { error }
  }
}

const CRON_JOBS_TAG = 'CronJobs'
const QUEUES_TAG = 'Queues'

export const homeAPi = createApi({
  reducerPath: 'homeAPi',
  baseQuery: customBaseQuery,
  tagTypes: [CRON_JOBS_TAG, QUEUES_TAG],
  endpoints: (builder) => ({
    getCronJobs: builder.query<ICronJobsResponse, void>({
      query: () => ({ url: '/cron-jobs', method: 'GET' }),
      providesTags: [CRON_JOBS_TAG],
    }),
    getCronJobRuns: builder.query<ICronJobRunsResponse, { id: number; page: number; per_page: number }>({
      query: ({ id, ...params }) => ({ url: `/cron-jobs/${id}`, method: 'GET', params }),
      providesTags: [CRON_JOBS_TAG],
    }),
    runCronJob: builder.mutation<IRunCronJobResponse, number>({
      query: (id) => ({ url: `/cron-jobs/${id}/run`, method: 'POST' }),
      invalidatesTags: [CRON_JOBS_TAG],
    }),
    getQueues: builder.query<IQueuesResponse, void>({
      query: () => ({ url: '/queues', method: 'GET' }),
      providesTags: [QUEUES_TAG],
    }),
    getQueueActivity: builder.query<IQueueActivityResponse, IQueueActivityParams>({
      query: (params) => ({ url: '/queues/activity', method: 'GET', params }),
      providesTags: [QUEUES_TAG],
    }),
    retryFailedJob: builder.mutation<{ status: boolean; message: string }, string>({
      query: (uuid) => ({ url: `/queues/failed/${uuid}/retry`, method: 'POST' }),
      invalidatesTags: [QUEUES_TAG],
    }),
    getListRoomsWithTvs: builder.query<IRoomTv[], { doctor_id: number; slot_id: number }>({
      query: (params) => ({
        url: `/rooms-tv-notify/${params.doctor_id}/${params.slot_id}`,
        method: 'GET',
        params,
      }),
    }),
    announceRoomsTVs: builder.mutation<void, { hardware_ids: number[]; message: string; number_of_dings: number }>({
      query: (data) => ({
        url: `/rooms-tv-notify`,
        method: 'POST',
        data,
      }),
    }),
  }),
})

export const {
  useGetListRoomsWithTvsQuery,
  useAnnounceRoomsTVsMutation,
  useGetCronJobsQuery,
  useGetCronJobRunsQuery,
  useRunCronJobMutation,
  useGetQueuesQuery,
  useGetQueueActivityQuery,
  useRetryFailedJobMutation,
} = homeAPi
